/**
 * Spec and commission briefs: Markdown in a fixed shape (## Description,
 * ## Spec, ## Use, ## Notes), written by staff and by agents over MCP. Where a
 * brief is read it renders as Markdown; where it's edited it stays raw text.
 */
import { Marked } from '../vendor/marked.js';

/** The link schemes a brief may use; anything else renders as plain text. */
const SAFE_LINK_SCHEMES = ['http:', 'https:', 'mailto:'];

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** @param {string} text */
function escapeHTML(text) {
    return String(text).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

/** @param {string} href */
function isSafeLink(href) {
    try {
        return SAFE_LINK_SCHEMES.includes(new URL(href).protocol);
    } catch {
        return false;
    }
}

// Briefs are written by any staff member and by agents, so unlike agreements
// they may not inject HTML: raw HTML shows as text, only web and mail links
// link, and images show their alt text rather than loading.
const briefMarked = new Marked({ gfm: true, breaks: true }).use({
    renderer: {
        html({ text }) {
            return escapeHTML(text);
        },
        link({ href, title, tokens }) {
            const text = this.parser.parseInline(tokens);
            if (!isSafeLink(href)) return text;
            const titleAttr = title ? ` title="${escapeHTML(title)}"` : '';
            return `<a href="${escapeHTML(href)}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
        },
        image({ text }) {
            return escapeHTML(text);
        },
    },
});

/**
 * A brief rendered to HTML, safe to set as an element's innerHTML; '' for none.
 * @param {string|null|undefined} text
 * @returns {string}
 */
export function briefHTML(text) {
    if (!text?.trim()) return '';
    return /** @type {string} */ (briefMarked.parse(text));
}

/** Tokens whose children are blocks, which read as separate words. */
const BLOCK_PARENTS = ['blockquote', 'list_item'];

/**
 * A token's text without its Markdown, as marked reads it: a link's text, an
 * image's alt text, a list's items.
 * @param {any} token
 * @returns {string}
 */
function plainText(token) {
    if (token.type === 'br' || token.type === 'space') return ' ';
    if (token.type === 'list') return token.items.map(plainText).join(' ');
    if (token.tokens)
        return token.tokens.map(plainText).join(BLOCK_PARENTS.includes(token.type) ? ' ' : '');
    return token.text ?? '';
}

/**
 * A brief as one line of plain text, for a grid cell: its content without
 * headings or Markdown, or its headings when that's all it has.
 * @param {string|null|undefined} text
 * @returns {string}
 */
export function briefPreview(text) {
    if (!text?.trim()) return '';
    const blocks = briefMarked.lexer(text);
    const body = blocks.filter((t) => t.type !== 'heading').map(plainText);
    const headings = blocks.filter((t) => t.type === 'heading').map(plainText);
    const words = (s) => s.join(' ').replace(/\s+/g, ' ').trim();
    return words(body) || words(headings);
}
